import type { ComponentProps, FC, PropsWithChildren } from 'react';

import {
  useGetEntitiesByFilterCallback,
  useGetEntityByRef,
  useGetEntityByRefCallback,
} from '@ipen-portal/plugin-common-react';
import stableStringify from 'json-stable-stringify';
import { useEffect, useMemo, useState } from 'react';
import { matchPath } from 'react-router';
import { useLocation } from 'react-router-dom';

import type { BreadcrumbDefinition } from '../../blueprints';
import type { BreadcrumbPartType } from '../../types';

import { BreadcrumbContext } from '../../context';
import { resolveBreadcrumbs } from './utils/resolveBreadcrumbs';

type Props = {
  breadcrumbDefinitions: readonly BreadcrumbDefinition[];
};

export const BreadcrumbProvider: FC<PropsWithChildren<Props>> = ({
  breadcrumbDefinitions,
  children,
}) => {
  const currentLocation = useLocation();
  const [breadcrumbs, setBreadcrumbs] = useState<
    readonly BreadcrumbPartType[]
  >([]);
  const getEntitiesByFilter = useGetEntitiesByFilterCallback();
  const getEntityByRef = useGetEntityByRefCallback();

  const {
    ['*']: subPath,
    kind,
    name,
    namespace,
  } = matchPath(
    { end: false, path: '/catalog/:namespace/:kind/:name/*' },
    currentLocation.pathname,
  )?.params ?? {};
  const { loading: entityLoading, value: entity } = useGetEntityByRef(
    kind && namespace && name ? { kind, name, namespace } : null,
  );

  const [breadcrumbsLoading, setBreadcrumbsLoading] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    const breadcrumbDefinition =
      entity && breadcrumbDefinitions.find(({ filter }) => filter(entity));
    if (breadcrumbDefinition) {
      setBreadcrumbsLoading(true);
      resolveBreadcrumbs(
        entity,
        subPath,
        getEntitiesByFilter,
        getEntityByRef,
        breadcrumbDefinitions,
      )
        .then(value => {
          if (isCancelled) {
            return;
          }

          setBreadcrumbs(prevState => {
            if (stableStringify(prevState) === stableStringify(value)) {
              return prevState;
            }
            return value;
          });
          setBreadcrumbsLoading(false);
        })
        .catch(() => {
          if (isCancelled) {
            return;
          }

          setBreadcrumbs([]);
          setBreadcrumbsLoading(false);
        });
    } else {
      setBreadcrumbs([]);
      setBreadcrumbsLoading(false);
    }
    return () => {
      isCancelled = true;
    };
  }, [
    entity,
    getEntitiesByFilter,
    getEntityByRef,
    subPath,
    breadcrumbDefinitions,
  ]);

  const loading = entityLoading || breadcrumbsLoading;
  const state = useMemo(
    (): ComponentProps<typeof BreadcrumbContext.Provider>['value'] => ({
      breadcrumbs,
      loading,
    }),
    [breadcrumbs, loading],
  );

  return (
    <BreadcrumbContext.Provider value={state}>
      {children}
    </BreadcrumbContext.Provider>
  );
};
